import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/shared/ui';
import './lists.css';
import { HISTORY_DATE_PRESETS, type HistoryDatePreset } from './datePresets';

/**
 * One-line filter bar above a history table/list (00-ds-09 §3, D148):
 * search box · date range · (payer · status via `children`) · right.
 * UI-only; the actual filtering/search is server-side (§8).
 */
export function FilterBar({
  search,
  onSearch,
  searchPlaceholder = 'Search patient, claim, check or amount',
  dateRange,
  onDateRange,
  showDateRange = true,
  searchDebounceMs = 0,
  children,
  right,
}: {
  search: string;
  onSearch: (v: string) => void;
  searchPlaceholder?: string;
  /** Omit the pair to hide the date-range select (e.g. reference lists). */
  dateRange?: HistoryDatePreset;
  onDateRange?: (v: HistoryDatePreset) => void;
  showDateRange?: boolean;
  /** Wait this long after the last keystroke before calling `onSearch` (0 = on every key). */
  searchDebounceMs?: number;
  /** Extra filters: payer / status selects. No location filter: every list is one location (D205). */
  children?: React.ReactNode;
  /** Right-aligned slot (e.g. an Export button). */
  right?: React.ReactNode;
}) {
  // The input keeps its own text so fast typing never loses letters while the
  // URL (the parent's state) catches up after a pause.
  const [text, setText] = useState(search);
  const pushed = useRef(search);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (search !== pushed.current) {
      pushed.current = search;
      setText(search);
    }
  }, [search]);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const onType = (v: string): void => {
    setText(v);
    if (timer.current) clearTimeout(timer.current);
    if (searchDebounceMs <= 0) {
      pushed.current = v;
      onSearch(v);
      return;
    }
    timer.current = setTimeout(() => {
      pushed.current = v;
      onSearch(v);
    }, searchDebounceMs);
  };

  return (
    <div className="lx-filterbar">
      <label className="lx-search">
        <Icon kind="search" size={16} aria-hidden />
        <input
          type="search"
          value={text}
          placeholder={searchPlaceholder}
          aria-label="Search this list"
          onChange={(e) => {
            onType(e.target.value);
          }}
        />
      </label>
      {showDateRange && dateRange !== undefined && onDateRange ? (
        <select
          className="lx-select"
          value={dateRange}
          aria-label="Date range"
          onChange={(e) => {
            onDateRange(e.target.value as HistoryDatePreset);
          }}
        >
          {HISTORY_DATE_PRESETS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
      ) : null}
      {children}
      {right ? <div className="lx-filterbar-right">{right}</div> : null}
    </div>
  );
}
